import React, { useContext, useEffect, useState } from "react";
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useConvexAuth, useMutation } from "convex/react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../../../convex/_generated/api";
import { MainRoutes } from "../../../../shared/models";
import { Button } from "../../../../catalyst-components/button";
import { Field, Label } from "../../../../catalyst-components/fieldset";
import { Input } from "../../../../catalyst-components/input";
import { Textarea } from "../../../../catalyst-components/textarea";
import { Alert, AlertActions, AlertDescription, AlertTitle } from "../../../../catalyst-components/alert";
import { Id } from "../../../../convex/_generated/dataModel";
import { BaseContext } from "../../../../shared/base-context";
import { useAuthToken } from "../../../../shared/hooks/useAuthToken";
import { useConvexSiteUrl } from "../../../../shared/hooks/useConvexSiteUrl";
import { Switch } from "../../../../catalyst-components/switch";
import { usePersistedForm } from "../../../../shared/hooks/usePersistedForm";
import { getJsonErrorContext } from "../../../../shared/helpers/utils";

export const ViewSystemAssistant: React.FC = () => {
    const { assistantId } = useParams();
    const { isAuthenticated } = useConvexAuth();
    const navigate = useNavigate();

    const assistant = useQuery(api.system_assistants.getById,
        assistantId ? { id: assistantId as Id<"system_assistants"> } : "skip"
    );

    const [formState, updateFormState] = usePersistedForm(`system-assistant-${assistantId || 'new'}`, {
        name: '',
        instructions: '',
        responseSchema: '',
        createOpenaiAssistant: false
    });

    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const updateSystemAssistant = useMutation(api.system_assistants.update);
    const { convexUserId } = useContext(BaseContext);
    const token = useAuthToken();
    const convexSiteUrl = useConvexSiteUrl();

    useEffect(() => {
        if (!isAuthenticated) {
            navigate(MainRoutes.Landing);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (assistant) {
            updateFormState({
                name: assistant.name,
                instructions: assistant.instructions || '',
                responseSchema: assistant.response_schema || '',
                createOpenaiAssistant: assistant.create_openai_assistant || false
            });
        }
    }, [assistant]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!assistant) return;

        try {
            JSON.parse(formState.responseSchema);
        } catch (error) {
            setAlertMessage(`Invalid response schema. Please enter a valid JSON object. ${getJsonErrorContext(error.message, formState.responseSchema)}`);
            setShowAlert(true);
            setSuccess(false);
            return;
        }

        try {
            await updateSystemAssistant({
                id: assistant._id,
                instructions: formState.instructions,
                response_schema: formState.responseSchema,
                user_id: convexUserId as Id<"users">,
                create_openai_assistant: formState.createOpenaiAssistant
            });
        } catch (error) {
            setAlertMessage('Error updating assistant in database. Please try again.');
            setShowAlert(true);
            setSuccess(false);
            return;
        }
        try {
            if (!formState.createOpenaiAssistant) {
                setAlertMessage('Assistant updated successfully!');
                setShowAlert(true);
                setSuccess(true);
                return;
            }

            const requestBody = {
                id: assistant?.openai_assistant_id,
                instructions: formState.instructions,
                response_schema: formState.responseSchema,
                systemAssistant: true,
                assistantId: assistant?._id
            }

            const response = await fetch(`${convexSiteUrl}/assistant/update`, {
                method: "POST",
                body: JSON.stringify(requestBody),
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to update assistant');
            }

            setAlertMessage('Assistant updated successfully!');
            setShowAlert(true);
            setSuccess(true);
        } catch (error) {
            setAlertMessage('Error updating assistant. Please try again.');
            setShowAlert(true);
            setSuccess(false);
        }
    };

    return (
        <section className="min-h-screen bg-background pt-24 px-6 flex flex-col">
            <div className="flex flex-row gap-4 mb-6">
                <Button color="light" onClick={() => navigate('../')}>
                    Back to system assistants
                </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <Field>
                    <Label>Name</Label>
                    <Input
                        value={formState.name}
                        disabled
                    />
                </Field>

                <Field>
                    <Label>Instructions</Label>
                    <Textarea
                        required
                        value={formState.instructions}
                        onChange={(e) => updateFormState({ ...formState, instructions: e.target.value })}
                        rows={10}
                    />
                </Field>

                <Field>
                    <Label>Response Schema (JSON)</Label>
                        <Textarea
                        value={formState.responseSchema}
                        onChange={(e) => updateFormState({ ...formState, responseSchema: e.target.value })}
                        rows={10}
                    />
                </Field>

                <Field className="flex flex-row items-center">
                    <Label className="mr-4">Create OpenAI Assistant</Label>
                    <Switch
                        checked={formState.createOpenaiAssistant}
                        onChange={() => updateFormState({ ...formState, createOpenaiAssistant: !formState.createOpenaiAssistant })}
                    />
                </Field>

                <Button color="light" type="submit">
                    Save Changes
                </Button>
            </form>

            <Alert open={showAlert} onClose={() => setShowAlert(false)} size="lg">
                <AlertTitle>Notification</AlertTitle>
                <AlertDescription>{alertMessage}</AlertDescription>
                <AlertActions>
                    <Button plain onClick={() => setShowAlert(false)}>
                        Ok
                    </Button>
                </AlertActions>
            </Alert>
        </section>
    );
}; 